/**
 * Template Editor Component
 * 
 * 3-panel structural editor for inspection templates:
 * - Left: Sortable systems list
 * - Middle: Sortable items for selected system
 * - Right: Item property editor
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInspectionTemplateEditor } from '@/hooks/useInspectionTemplateEditor';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TemplateSystemList } from './TemplateSystemList';
import { TemplateItemList } from './TemplateItemList';
import { TemplateItemEditor } from './TemplateItemEditor';
import { PublishTemplateDialog } from './PublishTemplateDialog';
import { TemplateVersionHistory } from './TemplateVersionHistory';
import {
  ArrowLeft,
  Save,
  Upload,
  Loader2,
  FileText,
  AlertCircle,
  History,
  Globe,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

interface TemplateEditorProps {
  templateId: string;
  shopId: string;
}

export function TemplateEditor({ templateId, shopId }: TemplateEditorProps) {
  const navigate = useNavigate();
  const editor = useInspectionTemplateEditor(shopId, templateId);
  const [selectedSystemId, setSelectedSystemId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);

  const template = editor.currentTemplate;
  const systems = editor.systems;
  const items = editor.items;
  const selectedSystem = systems.find((s) => s.id === selectedSystemId);
  const systemItems = items.filter((i) => i.system_id === selectedSystemId);
  const selectedItem = items.find((i) => i.id === selectedItemId);

  // Auto-select first system when loaded
  if (systems.length > 0 && !selectedSystemId) {
    setSelectedSystemId(systems[0].id);
  }

  if (editor.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="h-12 w-12 text-destructive mb-4" />
          <h3 className="text-lg font-medium mb-2">Template not found</h3>
          <Button variant="outline" onClick={() => navigate('/inspection-templates')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Templates
          </Button>
        </CardContent>
      </Card>
    );
  }

  const getStatusBadge = () => {
    if (template.status === 'draft') {
      return <Badge variant="outline" className="text-amber-600 border-amber-600">Draft</Badge>;
    }
    if (template.status === 'archived') {
      return <Badge variant="secondary">Archived</Badge>;
    }
    if (template.is_active) {
      return <Badge variant="default" className="bg-green-600">Active</Badge>;
    }
    return <Badge variant="secondary">v{template.version}</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/inspection-templates')}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  {template.is_global && (
                    <Tooltip>
                      <TooltipTrigger>
                        <Globe className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>Global template (read-only)</TooltipContent>
                    </Tooltip>
                  )}
                  <CardTitle>{template.name}</CardTitle>
                  {getStatusBadge()}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {template.description || 'No description'} · Version {template.version}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Version History */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm">
                    <History className="h-4 w-4 mr-2" />
                    History
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Version History</SheetTitle>
                  </SheetHeader>
                  <TemplateVersionHistory
                    versions={editor.versions}
                    currentVersionId={templateId}
                    onSelectVersion={(id) => navigate(`/inspection-templates/${id}`)}
                  />
                </SheetContent>
              </Sheet>

              {/* Publish (draft only) */}
              {editor.canEdit && (
                <Button
                  onClick={() => setPublishDialogOpen(true)}
                  disabled={editor.isPublishing || systems.length === 0}
                >
                  {editor.isPublishing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Publish
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Read-only warning for non-drafts */}
      {!editor.canEdit && !template.is_global && (
        <Card className="border-amber-500 bg-amber-500/10">
          <CardContent className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <span className="text-sm">
                This is a published template. Create a new draft to make changes.
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                editor.createDraft(template.template_key, template.name, template.description ?? undefined);
              }}
              disabled={editor.isCreatingDraft}
            >
              {editor.isCreatingDraft && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Draft
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 3-Panel Editor */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Systems */}
        <Card className="lg:col-span-1">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm font-medium">Systems</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <TemplateSystemList
              systems={systems}
              selectedSystemId={selectedSystemId}
              onSelectSystem={setSelectedSystemId}
              onAddSystem={editor.canEdit ? (data) => editor.addSystem(templateId, data) : undefined}
              onUpdateSystem={editor.canEdit ? editor.updateSystem : undefined}
              onDeleteSystem={editor.canEdit ? editor.deleteSystem : undefined}
              onReorderSystems={editor.canEdit ? (ids) => editor.reorderSystems(templateId, ids) : undefined}
              disabled={!editor.canEdit}
              isLoading={editor.isLoadingStructure}
            />
          </CardContent>
        </Card>

        {/* Middle: Items */}
        <Card className="lg:col-span-1">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm font-medium">
              {selectedSystem ? `Items in ${selectedSystem.display_name}` : 'Select a System'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            {selectedSystemId ? (
              <TemplateItemList
                items={systemItems}
                selectedItemId={selectedItemId}
                onSelectItem={setSelectedItemId}
                onAddItem={editor.canEdit ? (data) => editor.addItem(selectedSystemId, data) : undefined}
                onDeleteItem={editor.canEdit ? editor.deleteItem : undefined}
                onReorderItems={editor.canEdit ? (ids) => editor.reorderItems(selectedSystemId, ids) : undefined}
                disabled={!editor.canEdit}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <FileText className="h-8 w-8 mb-2" />
                <p className="text-sm">Select a system to view items</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right: Item Editor */}
        <Card className="lg:col-span-1">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm font-medium">
              {selectedItem ? 'Edit Item' : 'Item Properties'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {selectedItem ? (
              <TemplateItemEditor
                item={selectedItem}
                onUpdate={editor.canEdit ? async (updates) => {
                  await editor.updateItem(selectedItem.id, updates as any);
                } : undefined}
                disabled={!editor.canEdit}
                isSaving={editor.isSaving}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <FileText className="h-8 w-8 mb-2" />
                <p className="text-sm">Select an item to edit</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Publish Dialog */}
      <PublishTemplateDialog
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
        templateId={templateId}
        templateName={template.name}
        version={template.version}
        onPublish={async (changeNotes) => {
          await editor.publishDraft(templateId, changeNotes);
          setPublishDialogOpen(false);
        }}
        isPublishing={editor.isPublishing}
      />
    </div>
  );
}
